import { AlertType, Waiver } from '@types';
import { AlertModal, Card, PageHeader, SectionHeader } from '_atoms';
import { AdminService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import { MdEdit, MdSave } from 'react-icons/md';

const WaiverPage: React.FC = () => {
  const [waiver, setWaiver] = useState<Waiver>({
    id: '',
    waiverDetails: '',
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [hasExistingWaiver, setHasExistingWaiver] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleWaiverChange = (value: string) => {
    setWaiver(prev => ({
      ...prev,
      waiverDetails: value,
    }));
  };

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
  };

  const handleSaveWaiver = useCallback(async () => {
    setIsLoadingSave(true);

    try {
      await AdminService.updateWaiver(waiver);
      showAlert('success', 'Success', 'Waiver updated successfully!');
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating waiver:', error);
      showAlert('error', 'Error', 'Error updating waiver. Please try again.');
    } finally {
      setIsLoadingSave(false);
    }
  }, [waiver, showAlert]);

  // Load current waiver on page load
  useEffect(() => {
    const loadCurrentWaiver = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await AdminService.getCurrentWaiver()) as unknown as Waiver;

        if (responseData && responseData.waiverDetails) {
          setWaiver(responseData);
          setHasExistingWaiver(true);
          setIsEditMode(false);
        } else {
          setHasExistingWaiver(false);
          setIsEditMode(true);
          showAlert('info', 'Info', 'There is currently no waiver configured.');
        }
      } catch (error) {
        console.error('Error loading current waiver:', error);
        showAlert('error', 'Error', 'Failed to load current waiver.');
        setHasExistingWaiver(false);
        setIsEditMode(true);
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentWaiver();
  }, [showAlert]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdEdit}
        title="Waiver Management"
        description="Configure the waiver text that customers must agree to before purchasing tickets."
      />

      {/* Main Content */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading waiver...</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Waiver Content Section */}
          <Card className="flex flex-col">
            <SectionHeader
              title="Waiver Text"
              icon={MdEdit}
              showEditButton={hasExistingWaiver}
              isEditMode={isEditMode}
              onEditToggle={handleEditToggle}
            />

            {hasExistingWaiver && !isEditMode ? (
              // Read-only view
              <div className="mt-4">
                <div className="prose prose-sm max-w-none">
                  <div
                    className="w-full p-4 border border-gray-300 rounded-md bg-gray-50 text-gray-900 min-h-[200px] overflow-y-auto"
                    dangerouslySetInnerHTML={{
                      __html:
                        waiver.waiverDetails || 'No waiver text available.',
                    }}
                  />
                </div>
              </div>
            ) : (
              // Edit view
              <div className="mt-4 space-y-4">
                <div>
                  <label
                    htmlFor="waiverText"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Waiver Text (HTML supported)
                  </label>
                  <textarea
                    id="waiverText"
                    value={waiver.waiverDetails}
                    onChange={e => handleWaiverChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[300px] resize-y"
                    placeholder="Enter waiver text here... HTML tags are supported for formatting."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    You can use HTML tags for formatting (e.g., &lt;strong&gt;,
                    &lt;br&gt;, &lt;p&gt;)
                  </p>
                </div>

                {/* Preview Section */}
                {waiver.waiverDetails && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview
                    </label>
                    <div className="w-full p-4 border border-gray-300 rounded-md bg-gray-50 text-gray-900 min-h-[200px] overflow-y-auto">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: waiver.waiverDetails,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveWaiver}
                    disabled={isLoadingSave || !waiver.waiverDetails.trim()}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <MdSave className="mr-2" />
                    {isLoadingSave ? 'Saving...' : 'Save Waiver'}
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default WaiverPage;
